import {useEffect, useRef, useState, type CSSProperties, type ReactElement, type ReactNode} from 'react';
import {createPortal} from 'react-dom';
import {Tooltip, TooltipContent, TooltipTrigger} from '@/components/ui/tooltip';

export function Hint({
  children,
  content,
  side = 'top',
}: {
  children: ReactElement;
  content: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side}>{content}</TooltipContent>
    </Tooltip>
  );
}

// Rendered book content contains HTML supplied by the API. Delegation also covers
// abbreviations inserted after the book's first render without retaining DOM nodes.
export function ContentHints({
  target,
  content,
  children,
  side = 'top',
  shouldShow,
  shouldHide,
  style,
}: {
  target: string;
  content?: ReactNode;
  children?: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  shouldShow?: (event: {target: HTMLElement}) => boolean | void;
  shouldHide?: (event: {target: HTMLElement}) => boolean | void;
  style?: CSSProperties;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const anchorRef = useRef<HTMLElement | null>(null);
  const callbacks = useRef({shouldShow, shouldHide});
  callbacks.current = {shouldShow, shouldHide};
  useEffect(() => {
    const show = (event: Event) => {
      const element = event.target instanceof Element ? event.target.closest<HTMLElement>(target) : null;
      if (element && callbacks.current.shouldShow?.({target: element}) !== false) {
        anchorRef.current = element;
        setAnchor(element);
      }
    };
    const hide = (event: Event) => {
      const element = anchorRef.current;
      if (element && callbacks.current.shouldHide?.({target: element}) !== false) {
        anchorRef.current = null;
        setAnchor(null);
      }
    };
    document.addEventListener('pointerover', show);
    document.addEventListener('focusin', show);
    document.addEventListener('pointerout', hide);
    document.addEventListener('focusout', hide);
    window.addEventListener('scroll', hide, true);
    return () => {
      document.removeEventListener('pointerover', show);
      document.removeEventListener('focusin', show);
      document.removeEventListener('pointerout', hide);
      document.removeEventListener('focusout', hide);
      window.removeEventListener('scroll', hide, true);
    };
  }, [target]);
  if (!anchor) return null;
  const rect = anchor.getBoundingClientRect();
  return createPortal(
    <Tooltip open>
      <TooltipTrigger asChild>
        <span
          aria-hidden
          style={{
            position: 'fixed',
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
            pointerEvents: 'none',
          }}
        />
      </TooltipTrigger>
      <TooltipContent side={side} style={style}>
        {content ?? children ?? anchor.dataset.hint ?? anchor.getAttribute('title')}
      </TooltipContent>
    </Tooltip>,
    document.body,
  );
}
